import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { MessageCircle, Loader2, Send, ArrowLeft, Car, MoreVertical, Flag, Ban, ShieldOff } from "lucide-react";

const REPORT_REASONS = ["Golpe ou fraude", "Spam ou propaganda", "Conteúdo ofensivo", "Outro"];

function timeAgo(iso) {
  try {
    const diff = (Date.now() - new Date(iso)) / 1000;
    if (diff < 60) return "agora";
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch { return ""; }
}

export default function Mensagens() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const endRef = useRef(null);
  const activeId = params.get("c");

  const loadList = useCallback(() => {
    api.get("/conversations").then(({ data }) => setConvs(data)).finally(() => setLoadingList(false));
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openConv = useCallback((cid) => {
    setParams({ c: cid });
    api.get(`/conversations/${cid}`).then(({ data }) => {
      setActive(data);
      setConvs((prev) => prev.map((c) => (c.id === cid ? { ...c, unread: 0 } : c)));
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    });
  }, [setParams]);

  useEffect(() => { if (activeId) openConv(activeId); /* eslint-disable-next-line */ }, [activeId]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    const t = text.trim();
    setText("");
    try {
      await api.post(`/conversations/${active.id}/message`, { text: t });
      const { data } = await api.get(`/conversations/${active.id}`);
      setActive(data);
      loadList();
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } finally { setSending(false); }
  };

  const doBlock = async () => {
    if (!active) return;
    setBlockBusy(true);
    try {
      const { data } = await api.post(`/conversations/${active.id}/block`);
      setActive((a) => ({ ...a, ...data }));
      toast.success("Usuário bloqueado. Ele não poderá mais te enviar mensagens.");
    } catch (e) { toast.error("Não foi possível bloquear."); }
    finally { setBlockBusy(false); }
  };

  const doUnblock = async () => {
    if (!active) return;
    setBlockBusy(true);
    try {
      const { data } = await api.post(`/conversations/${active.id}/unblock`);
      setActive((a) => ({ ...a, ...data }));
      toast.success("Usuário desbloqueado.");
    } catch (e) { toast.error("Não foi possível desbloquear."); }
    finally { setBlockBusy(false); }
  };

  const submitReport = async () => {
    if (!active) return;
    setReportBusy(true);
    try {
      const { data } = await api.post(`/conversations/${active.id}/report`, { reason: reportReason, note: reportNote.trim() });
      setActive((a) => ({ ...a, ...data }));
      setReportOpen(false);
      setReportNote("");
      setReportReason(REPORT_REASONS[0]);
      toast.success("Denúncia enviada. O usuário foi bloqueado.");
    } catch (e) { toast.error("Não foi possível enviar a denúncia."); }
    finally { setReportBusy(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8" data-testid="mensagens-page">
      <p className="overline text-blaze mb-2">Chat seguro</p>
      <h1 className="font-heading font-800 text-3xl md:text-4xl mb-6">Mensagens</h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 bg-white border border-border rounded-sm overflow-hidden" style={{ minHeight: "60vh" }}>
        {/* LIST */}
        <div className={`md:col-span-4 border-r border-border ${active ? "hidden md:block" : ""}`}>
          {loadingList ? (
            <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-blaze" /></div>
          ) : convs.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <MessageCircle className="w-8 h-8 mx-auto mb-3 text-muted-foreground" strokeWidth={1.5} />
              Nenhuma conversa ainda. Envie uma mensagem em um anúncio para começar.
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[70vh] overflow-auto">
              {convs.map((c) => (
                <button key={c.id} onClick={() => openConv(c.id)} data-testid={`conv-${c.id}`}
                  className={`w-full text-left p-4 flex gap-3 hover:bg-secondary/50 transition-colors ${active?.id === c.id ? "bg-secondary/60" : ""}`}>
                  <div className="w-12 h-12 rounded-sm overflow-hidden bg-secondary shrink-0">
                    {c.listing_image ? <img src={fileUrl(c.listing_image)} alt="" className="w-full h-full object-cover" /> : <Car className="w-6 h-6 m-3 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-700 text-sm truncate">{c.other_name}</p>
                      <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(c.last_at)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{c.listing_title}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_text}</p>
                  </div>
                  {c.unread > 0 && <span className="w-5 h-5 rounded-full bg-blaze text-white text-[10px] font-700 flex items-center justify-center shrink-0" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* THREAD */}
        <div className={`md:col-span-8 flex flex-col ${active ? "" : "hidden md:flex"}`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">Selecione uma conversa</div>
          ) : (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <button className="md:hidden" onClick={() => { setActive(null); setParams({}); }} data-testid="thread-back"><ArrowLeft className="w-5 h-5" /></button>
                <div className="w-10 h-10 rounded-sm overflow-hidden bg-secondary shrink-0">
                  {active.listing_image ? <img src={fileUrl(active.listing_image)} alt="" className="w-full h-full object-cover" /> : <Car className="w-5 h-5 m-2.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-700 text-sm truncate">{active.other_name}</p>
                  <button className="text-xs text-blaze truncate hover:underline" onClick={() => navigate(`/anuncio/${active.listing_id}`)} data-testid="thread-listing-link">{active.listing_title}</button>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-9 h-9 rounded-sm hover:bg-secondary flex items-center justify-center transition-colors shrink-0" data-testid="thread-menu-btn" aria-label="Mais opções">
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="thread-report-btn">
                      <Flag className="w-4 h-4 mr-2" /> Denunciar conversa
                    </DropdownMenuItem>
                    {active.i_blocked ? (
                      <DropdownMenuItem onClick={doUnblock} disabled={blockBusy} data-testid="thread-unblock-btn">
                        <ShieldOff className="w-4 h-4 mr-2" /> Desbloquear usuário
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={doBlock} disabled={blockBusy} className="text-blaze" data-testid="thread-block-btn">
                        <Ban className="w-4 h-4 mr-2" /> Bloquear usuário
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="flex-1 overflow-auto p-4 space-y-3 bg-secondary/30" style={{ maxHeight: "48vh" }} data-testid="thread-messages">
                {active.messages.map((m) => {
                  const mine = m.sender_id === active.me;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-sm px-3 py-2 ${mine ? "bg-obsidian text-white" : "bg-white border border-border"}`}>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-muted-foreground"}`}>{timeAgo(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <div className="p-3 border-t border-border">
                {active.blocked ? (
                  <div className="flex items-center justify-between gap-3 bg-secondary/60 rounded-sm px-4 py-3" data-testid="thread-blocked-banner">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Ban className="w-4 h-4 text-blaze shrink-0" />
                      {active.i_blocked
                        ? "Você bloqueou este usuário. Vocês não podem trocar mensagens."
                        : "Não é possível enviar mensagens nesta conversa."}
                    </p>
                    {active.i_blocked && (
                      <Button variant="outline" size="sm" className="rounded-sm shrink-0" onClick={doUnblock} disabled={blockBusy} data-testid="thread-unblock-inline-btn">
                        {blockBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Desbloquear"}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                      placeholder="Escreva uma mensagem..." className="rounded-sm" data-testid="reply-input" />
                    <Button className="bg-blaze hover:bg-obsidian text-white rounded-sm shrink-0" onClick={send} disabled={sending || !text.trim()} data-testid="reply-send-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="rounded-sm" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Denunciar conversa</DialogTitle>
            <DialogDescription>
              Sua denúncia será enviada para revisão e o usuário será bloqueado automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <RadioGroup value={reportReason} onValueChange={setReportReason} data-testid="report-reasons">
              {REPORT_REASONS.map((r) => (
                <div key={r} className="flex items-center gap-2">
                  <RadioGroupItem value={r} id={`reason-${r}`} data-testid={`report-reason-${r}`} />
                  <Label htmlFor={`reason-${r}`} className="text-sm font-500 cursor-pointer">{r}</Label>
                </div>
              ))}
            </RadioGroup>
            <Textarea value={reportNote} onChange={(e) => setReportNote(e.target.value)} maxLength={1000}
              placeholder="Descreva o que aconteceu (opcional)" className="rounded-sm" rows={3} data-testid="report-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setReportOpen(false)} data-testid="report-cancel-btn">Cancelar</Button>
            <Button className="bg-blaze hover:bg-obsidian text-white rounded-sm" onClick={submitReport} disabled={reportBusy} data-testid="report-submit-btn">
              {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar denúncia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
